import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Helmet } from 'react-helmet';
import {
  Building2,
  CheckCircle2,
  Clock,
  Briefcase,
  GraduationCap,
  PoundSterling,
  Stethoscope,
  ArrowRight,
  ChevronRight,
  AlertTriangle,
  Users,
  FileText,
  Target,
  MapPin,
  Shield,
} from 'lucide-react';

const LEDGuidePage = () => {
  const publishDate = '2025-01-15';
  const modifiedDate = new Date().toISOString().split('T')[0];

  const whatIsLED = [
    {
      icon: Building2,
      title: 'Locally Employed',
      description: 'LED doctors are employed directly by individual NHS Trusts, not through national training programmes. Your employer is the Trust itself.',
    },
    {
      icon: Stethoscope,
      title: 'Clinical Service Posts',
      description: 'These are service-focused roles providing direct patient care. You\'ll work alongside training-grade doctors doing similar clinical work.',
    },
    {
      icon: GraduationCap,
      title: 'Non-Training Grade',
      description: 'LED positions are outside the formal training pathway, which means they\'re not subject to the 2026 Prioritisation Act for IMGs.',
    },
    {
      icon: Clock,
      title: 'Flexible Contracts',
      description: 'Contracts range from 6 months to permanent. Many IMGs use LED roles as a stepping stone while building NHS experience.',
    },
  ];

  const ledVsTraining = [
    {
      aspect: 'Application Process',
      led: 'Apply directly to NHS Trusts via TRAC/NHS Jobs',
      training: 'National application through Oriel/HEE',
    },
    {
      aspect: 'Competition',
      led: 'Local competition only - equal access for IMGs',
      training: 'National competition - UK graduates prioritised from 2026',
    },
    {
      aspect: 'Visa Sponsorship',
      led: 'Many Trusts sponsor Skilled Worker visas',
      training: 'Visa sponsorship varies by programme',
    },
    {
      aspect: 'Contract Length',
      led: '6 months to permanent',
      training: 'Fixed rotation periods (typically 4-6 months each)',
    },
    {
      aspect: 'Location',
      led: 'You choose - apply to specific Trusts',
      training: 'Allocated based on preferences and ranking',
    },
    {
      aspect: 'Progression',
      led: 'Based on experience and interviews',
      training: 'Structured ARCP progression',
    },
  ];

  const benefits = [
    {
      title: 'Equal Access for IMGs',
      description: 'LED jobs are not affected by the Medical Training Prioritisation Act 2026. You compete on merit with all other applicants.',
      highlight: true,
      link: '/img-guide/medical-training-prioritisation-act-2026',
      linkText: 'Learn about the Act →',
    },
    {
      title: 'Build NHS Experience',
      description: 'Every month in an LED role adds to your NHS experience, which may qualify you for secondary priority in future training applications.',
      highlight: false,
    },
    {
      title: 'Choose Your Location',
      description: 'Apply to specific Trusts in cities and regions where you want to work. No allocation system.',
      highlight: false,
    },
    {
      title: 'Visa Sponsorship',
      description: 'Many NHS Trusts are licensed sponsors and actively recruit internationally for LED positions.',
      highlight: false,
    },
    {
      title: 'Competitive Salary',
      description: 'LED doctors are typically paid on the same NHS pay scales as training-grade doctors at equivalent levels.',
      highlight: false,
    },
    {
      title: 'Specialty Experience',
      description: 'Choose LED roles in your target specialty to build relevant experience for future training applications.',
      highlight: false,
    },
  ];

  const howToGetLED = [
    {
      step: '1',
      title: 'Get GMC Registration',
      description: 'You need GMC registration with a licence to practise. Most IMGs achieve this through PLAB 1 & 2 or by having their qualification recognised.',
    },
    {
      step: '2',
      title: 'Set Up Job Alerts',
      description: 'LED positions are posted on TRAC and NHS Jobs. Use TRACJobsAlert to get instant notifications the moment new roles appear.',
    },
    {
      step: '3',
      title: 'Prepare Strong Applications',
      description: 'Tailor your CV and personal statement for each application. Highlight relevant experience and demonstrate knowledge of the Trust.',
    },
    {
      step: '4',
      title: 'Apply Fast',
      description: 'Popular LED posts receive many applications within 24-48 hours. When you get an alert, apply immediately with your prepared materials.',
    },
  ];

  const faqs = [
    {
      question: 'What does LED stand for in NHS jobs?',
      answer: 'LED stands for Locally Employed Doctor. These are doctors employed directly by individual NHS Trusts rather than through national training programmes managed by Health Education England.',
    },
    {
      question: 'What is the difference between LED and Trust Grade?',
      answer: 'LED and Trust Grade are essentially the same thing - different Trusts use different terminology. Both refer to non-training NHS doctor posts employed directly by Trusts. Some Trusts also use terms like "Trust Doctor" or "Clinical Fellow".',
    },
    {
      question: 'Can LED experience count towards specialty training?',
      answer: 'Yes. NHS experience in LED roles can strengthen your training application portfolio and may qualify you for secondary priority consideration under the 2026 Prioritisation Act. The clinical experience you gain is also valuable for demonstrating competence.',
    },
    {
      question: 'Do LED doctors get paid the same as training doctors?',
      answer: 'Generally yes. LED doctors are typically paid on the same NHS Medical & Dental pay scales. The exact grade depends on your experience level - you could be employed at SHO-equivalent, Registrar-equivalent, or Staff Grade level.',
    },
    {
      question: 'How long are LED contracts?',
      answer: 'LED contracts vary from 6 months to permanent positions. Many Trusts offer initial 12-month contracts with the option to extend. Some LED posts are substantive (permanent) positions.',
    },
    {
      question: 'Can I get visa sponsorship for LED jobs?',
      answer: 'Yes, many NHS Trusts are licensed Skilled Worker visa sponsors and actively recruit internationally for LED positions. Check the job advert or contact the Trust\'s HR department to confirm sponsorship availability.',
    },
  ];

  return (
    <>
      <Helmet>
        <title>LED Doctor Jobs UK for IMGs | NHS Job Alerts — TRACJobsAlert</title>
        <meta name="description" content="Find Locally Employed Doctor (LED) jobs across the NHS. LED positions are fully open to IMGs, not affected by the 2026 Prioritisation Act. Get instant alerts for new LED vacancies." />
        <meta name="keywords" content="LED doctor jobs UK, Locally Employed Doctor NHS, LED jobs for IMGs, LED vs Trust Grade, LED doctor salary NHS, LED position NHS, NHS LED jobs international doctors" />
        <link rel="canonical" href="https://tracjobsalert.co.uk/img-guide/led-jobs" />
        
        {/* Open Graph */}
        <meta property="og:type" content="article" />
        <meta property="og:url" content="https://tracjobsalert.co.uk/img-guide/led-jobs" />
        <meta property="og:title" content="LED Doctor Jobs UK for IMGs | NHS Job Alerts" />
        <meta property="og:description" content="Find Locally Employed Doctor jobs across the NHS. Fully open to IMGs, not affected by the 2026 Prioritisation Act." />
        <meta property="og:image" content="https://tracjobsalert.co.uk/icon-512.png" />
        <meta property="og:site_name" content="TRACJobsAlert" />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content="LED Doctor Jobs UK for IMGs | TRACJobsAlert" />
        <meta name="twitter:description" content="Complete guide to Locally Employed Doctor positions for international medical graduates." />
        
        {/* Article Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            "headline": "LED Doctor Jobs UK: Complete Guide for International Medical Graduates",
            "description": "Everything you need to know about Locally Employed Doctor (LED) positions in the NHS - what they are, how to apply, pay scales, and why they're ideal for IMGs.",
            "author": {
              "@type": "Organization",
              "name": "TRACJobsAlert"
            },
            "publisher": {
              "@type": "Organization",
              "name": "TRACJobsAlert",
              "logo": {
                "@type": "ImageObject",
                "url": "https://tracjobsalert.co.uk/icon-512.png"
              }
            },
            "datePublished": publishDate,
            "dateModified": modifiedDate,
            "mainEntityOfPage": "https://tracjobsalert.co.uk/img-guide/led-jobs"
          })}
        </script>
        
        {/* FAQ Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": faqs.map(faq => ({
              "@type": "Question",
              "name": faq.question,
              "acceptedAnswer": {
                "@type": "Answer",
                "text": faq.answer
              }
            }))
          })}
        </script>
      </Helmet>

      <div className="min-h-screen bg-white">
        {/* Header */}
        <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center h-16">
              <Link to="/" className="flex items-center gap-2">
                <div className="w-10 h-10 bg-emerald-500 rounded-lg flex items-center justify-center">
                  <Stethoscope className="w-6 h-6 text-white" />
                </div>
                <div>
                  <span className="font-bold text-slate-900">Trac Jobs Alert</span>
                  <span className="text-xs text-emerald-600 block -mt-1">For Doctors</span>
                </div>
              </Link>
              <div className="flex items-center gap-3">
                <Link to="/login">
                  <Button variant="ghost" className="text-slate-600">Sign In</Button>
                </Link>
                <Link to="/register">
                  <Button className="bg-emerald-500 hover:bg-emerald-600 text-white rounded-full">
                    Get Alerts
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </header>

        {/* Breadcrumb */}
        <div className="bg-slate-50 border-b border-slate-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
            <nav className="flex items-center gap-2 text-sm">
              <Link to="/" className="text-slate-500 hover:text-emerald-600">Home</Link>
              <ChevronRight className="w-4 h-4 text-slate-400" />
              <Link to="/img-guide" className="text-slate-500 hover:text-emerald-600">IMG Guide</Link>
              <ChevronRight className="w-4 h-4 text-slate-400" />
              <span className="text-slate-900 font-medium">LED Jobs</span>
            </nav>
          </div>
        </div>

        {/* Hero */}
        <section className="bg-gradient-to-b from-slate-50 to-white py-12 lg:py-16">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-50 border border-blue-200 text-blue-700 text-sm font-medium mb-6">
              <Building2 className="w-4 h-4" />
              Locally Employed Doctor
            </div>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 mb-6">
              LED Doctor Jobs UK for IMGs
            </h1>
            <p className="text-lg sm:text-xl text-slate-600 mb-8 max-w-3xl mx-auto">
              Locally Employed Doctor positions are your direct route into the NHS. Not affected by the <Link to="/img-guide/medical-training-prioritisation-act-2026" className="text-emerald-600 hover:text-emerald-700 underline">2026 Prioritisation Act</Link>, 
              with visa sponsorship available, LED jobs offer IMGs equal opportunity to build their UK medical career.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/register">
                <Button className="bg-emerald-500 hover:bg-emerald-600 text-white rounded-full px-8 py-6 text-lg">
                  Get LED Job Alerts
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        </section>

        {/* What is LED */}
        <section className="py-12 lg:py-16 px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-4 text-center">
              What is a Locally Employed Doctor (LED)?
            </h2>
            <p className="text-lg text-slate-600 max-w-3xl mx-auto text-center mb-12">
              LED is the official NHS term for doctors employed directly by Trusts outside of national training programmes.
            </p>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {whatIsLED.map((item, index) => (
                <div key={index} className="bg-slate-50 rounded-xl p-6 border border-slate-200">
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                    <item.icon className="w-6 h-6 text-blue-600" />
                  </div>
                  <h3 className="font-semibold text-lg text-slate-900 mb-2">{item.title}</h3>
                  <p className="text-slate-600 text-sm">{item.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* LED vs Training Comparison */}
        <section className="py-12 lg:py-16 px-4 sm:px-6 lg:px-8 bg-slate-50">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-4 text-center">
              LED Jobs vs Training Posts
            </h2>
            <p className="text-lg text-slate-600 max-w-3xl mx-auto text-center mb-12">
              Understanding the key differences helps you make the right career choice.
            </p>
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
              <div className="grid grid-cols-3 bg-slate-100 font-semibold text-slate-900">
                <div className="p-4 border-r border-slate-200">Aspect</div>
                <div className="p-4 border-r border-slate-200 text-blue-600">LED Position</div>
                <div className="p-4 text-purple-600">Training Post</div>
              </div>
              {ledVsTraining.map((row, index) => (
                <div key={index} className="grid grid-cols-3 border-t border-slate-200">
                  <div className="p-4 border-r border-slate-200 font-medium text-slate-900">{row.aspect}</div>
                  <div className="p-4 border-r border-slate-200 text-slate-600 text-sm">{row.led}</div>
                  <div className="p-4 text-slate-600 text-sm">{row.training}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Benefits */}
        <section className="py-12 lg:py-16 px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-4 text-center">
              Why LED Jobs are Perfect for IMGs
            </h2>
            <p className="text-lg text-slate-600 max-w-3xl mx-auto text-center mb-12">
              LED positions offer unique advantages that make them the best route for international medical graduates.
            </p>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {benefits.map((benefit, index) => (
                <div 
                  key={index} 
                  className={`rounded-xl p-6 border ${benefit.highlight ? 'bg-emerald-50 border-emerald-200' : 'bg-white border-slate-200'}`}
                >
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className={`w-6 h-6 mt-0.5 flex-shrink-0 ${benefit.highlight ? 'text-emerald-600' : 'text-emerald-500'}`} />
                    <div>
                      <h3 className="font-semibold text-lg text-slate-900 mb-2">{benefit.title}</h3>
                      <p className="text-slate-600 text-sm">{benefit.description}</p>
                      {benefit.link && (
                        <Link to={benefit.link} className="text-emerald-600 text-sm font-medium mt-2 inline-flex items-center gap-1 hover:text-emerald-700">
                          {benefit.linkText}
                        </Link>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* How to Get LED Job */}
        <section className="py-12 lg:py-16 px-4 sm:px-6 lg:px-8 bg-slate-50">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-4 text-center">
              How to Get an LED Job
            </h2>
            <p className="text-lg text-slate-600 max-w-3xl mx-auto text-center mb-12">
              Follow these steps to secure your LED position in the NHS.
            </p>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {howToGetLED.map((item, index) => (
                <div key={index} className="bg-white rounded-xl p-6 border border-slate-200">
                  <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center text-white font-bold text-lg mb-4">
                    {item.step}
                  </div>
                  <h3 className="font-semibold text-lg text-slate-900 mb-2">{item.title}</h3>
                  <p className="text-slate-600 text-sm">{item.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-12 lg:py-16 px-4 sm:px-6 lg:px-8 bg-blue-600">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4">
              Get Instant LED Job Alerts
            </h2>
            <p className="text-lg text-blue-100 mb-8 max-w-2xl mx-auto">
              LED positions fill fast. Be the first to know when new jobs matching your criteria are posted on NHS Jobs and TRAC.
            </p>
            <Link to="/register">
              <Button className="bg-white text-blue-600 hover:bg-blue-50 rounded-full px-8 py-6 text-lg font-semibold">
                Start Getting Alerts — £9.99/month
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
            <p className="text-blue-200 text-sm mt-4">Cancel anytime. No contract.</p>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-12 lg:py-16 px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-4 text-center">
              Frequently Asked Questions
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto text-center mb-12">
              Common questions about LED doctor positions in the NHS.
            </p>
            <div className="space-y-4">
              {faqs.map((faq, index) => (
                <div key={index} className="bg-slate-50 rounded-xl p-6 border border-slate-200">
                  <h3 className="font-semibold text-lg text-slate-900 mb-2">{faq.question}</h3>
                  <p className="text-slate-600">{faq.answer}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Related Links */}
        <section className="py-12 lg:py-16 px-4 sm:px-6 lg:px-8 bg-slate-50">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-2xl font-bold text-slate-900 mb-8 text-center">Related Guides</h2>
            <div className="grid md:grid-cols-3 gap-6">
              <Link to="/img-guide/trust-grade-jobs" className="bg-white rounded-xl p-6 border border-slate-200 hover:border-emerald-500 transition-colors">
                <h3 className="font-semibold text-lg text-slate-900 mb-2">Trust Grade Jobs</h3>
                <p className="text-slate-600 text-sm mb-4">Complete guide to Trust Grade doctor positions.</p>
                <span className="text-emerald-600 text-sm font-medium flex items-center gap-1">
                  Read guide <ChevronRight className="w-4 h-4" />
                </span>
              </Link>
              <Link to="/img-guide/medical-training-prioritisation-act-2026" className="bg-white rounded-xl p-6 border border-slate-200 hover:border-emerald-500 transition-colors">
                <h3 className="font-semibold text-lg text-slate-900 mb-2">Medical Training Prioritisation Act</h3>
                <p className="text-slate-600 text-sm mb-4">What the 2026 Act means for your career.</p>
                <span className="text-emerald-600 text-sm font-medium flex items-center gap-1">
                  Read guide <ChevronRight className="w-4 h-4" />
                </span>
              </Link>
              <Link to="/img-guide/plab-jobs" className="bg-white rounded-xl p-6 border border-slate-200 hover:border-emerald-500 transition-colors">
                <h3 className="font-semibold text-lg text-slate-900 mb-2">PLAB Doctor Jobs</h3>
                <p className="text-slate-600 text-sm mb-4">Finding NHS jobs after passing PLAB.</p>
                <span className="text-emerald-600 text-sm font-medium flex items-center gap-1">
                  Read guide <ChevronRight className="w-4 h-4" />
                </span>
              </Link>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="bg-slate-900 py-12 px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col md:flex-row justify-between items-center gap-6">
              <div className="flex items-center gap-2">
                <div className="w-10 h-10 bg-emerald-500 rounded-lg flex items-center justify-center">
                  <Stethoscope className="w-6 h-6 text-white" />
                </div>
                <div>
                  <span className="font-bold text-white">Trac Jobs Alert</span>
                  <span className="text-xs text-emerald-400 block">For Doctors</span>
                </div>
              </div>
              <div className="flex flex-wrap gap-6 text-sm">
                <Link to="/contact" className="text-slate-400 hover:text-white">Contact</Link>
                <Link to="/privacy" className="text-slate-400 hover:text-white">Privacy Policy</Link>
                <Link to="/terms" className="text-slate-400 hover:text-white">Terms of Service</Link>
                <Link to="/img-guide" className="text-slate-400 hover:text-white">IMG Guide</Link>
              </div>
            </div>
            <div className="mt-8 pt-8 border-t border-slate-800 text-center text-slate-500 text-sm">
              © 2025 TRACJobsAlert. All rights reserved.
            </div>
          </div>
        </footer>
      </div>
    </>
  );
};

export default LEDGuidePage;
